import React, { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import { groupBy, sortBy, uniqBy } from 'lodash'
import { getScrolling } from '../utils'
import _this from '../constants'
import './index.css'

const DashboardLogsActionsByModule = ({ chartID, dataSet }) => {
  useLayoutEffect(() => {
    let container = document.querySelector(`#${chartID}`)
    let viewport = container.getBoundingClientRect()

    // Get global constants.
    const { FONT_SIZE_AXIS, MIN_GRID_VALUE, PRIMARY_COLOR, MARGIN_CHART_CONTAINER } = _this
    MARGIN_CHART_CONTAINER.paddingTop = 0
    MARGIN_CHART_CONTAINER.paddingBottom = -7
    MARGIN_CHART_CONTAINER.paddingLeft = 0
    MARGIN_CHART_CONTAINER.paddingRight = 5

    // Build chart data from pre-aggregated summary.
    let dataTotalsActionsByModule = (dataSet.byModule || []).map((item, _key) => ({
      index: _key,
      action: item.action,
      count: item.count,
      module: item.module,
      categoryLevel: item.module + '/' + item.action,
      columnSettings: { fill: PRIMARY_COLOR }
    }))

    // Sort data.
    dataTotalsActionsByModule = sortBy(dataTotalsActionsByModule, ['module', 'count', 'index'])

    const dataTempRange = groupBy(dataTotalsActionsByModule, 'module')
    const dataRange = Object.keys(dataTempRange).map((item, key) => {
      const temp = dataTempRange[item].sort((a, b) => a.count + a.index - (b.count + b.index))
      return {
        startRangeModule: temp[0],
        endRangeModule: temp[temp.length - 1]
      }
    })

    // Data sort and group by.
    const dataSortIndex = uniqBy(dataTotalsActionsByModule, 'count')
      .map((item) => item.count)
      .sort((a, b) => b - a)

    // Set root instance.
    let root = am5.Root.new(chartID)

    // Set theme.
    root.setThemes([am5themes_Animated.new(root)])

    // Remove logo.
    root._logo.dispose() // Create wrapper container

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'zoomX',
        wheelY: 'panY',
        ...MARGIN_CHART_CONTAINER
      })
    )

    // Add cursor.
    let cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}))
    cursor.lineY.set('visible', false)
    cursor.lineX.set('visible', false)

    // Dissable scrollbar.
    chart.set(
      'scrollbarX',
      am5.Scrollbar.new(root, {
        orientation: 'horizontal',
        visible: true
      })
    )
    chart.set(
      'scrollbarY',
      am5.Scrollbar.new(root, {
        orientation: 'vertical',
        visible: true,
        start: 0,
        end: getScrolling(dataTotalsActionsByModule.length)
      })
    )

    // Create X-axis.
    let xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        forceHidden: false,
        strictMinMax: true,
        forceInactive: true,
        maxPrecision: 0,
        extraMax: 0.1,
        min: 0,
        numberFormatter: am5.NumberFormatter.new(root, {
          numberFormat: '#,###a' // abbreviate: 1,000 → 1k, 10,000 → 10k
        }),
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50 // wider spacing prevents label overlap on large scales
        })
      })
    )

    xAxis.get('renderer').labels.template.setAll({
      oversizedBehavior: 'none', // don't truncate — let the formatter keep values short
      paddingTop: 5,
      textAlign: 'center',
      fontSize: FONT_SIZE_AXIS,
      fontWeight: 'normal'
    })

    xAxis.children.moveValue(
      am5.Label.new(root, {
        text: 'Number of actions by module',
        x: am5.p50,
        centerX: am5.p100,
        fontWeight: 'bold'
      })
    )

    // Create Y-axis-module.
    let yAxisModule = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        maxDeviation: 0,
        categoryField: 'categoryLevel',
        fixAxisSize: true,
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: MIN_GRID_VALUE
        })
      })
    )

    yAxisModule.get('renderer').labels.template.setAll({
      oversizedBehavior: 'none',
      maskContent: true,
      breakWordsnm: true,
      textAlign: 'start',
      fontSize: FONT_SIZE_AXIS,
      centerX: 0,
      fontWeight: 'normal'
    })

    yAxisModule.get('renderer').labels.template.adapters.add('text', (text, _target) => {
      if (text === '{category}') {
        return ''
      }
      return text
    })

    // Create Y-axis-action.
    let yAxisAction = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        maxDeviation: 0,
        categoryField: 'categoryLevel',
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: MIN_GRID_VALUE
        })
      })
    )

    yAxisAction.get('renderer').labels.template.setAll({
      oversizedBehavior: 'none',
      maskContent: true,
      breakWordsnm: true,
      textAlign: 'start',
      fontSize: FONT_SIZE_AXIS,
      centerX: 0,
      fontWeight: 'normal',
      paddingLeft: 50,
      paddingRight: 10
    })

    // Show only the action part of 'module/action' key
    yAxisAction.get('renderer').labels.template.adapters.add('text', (text) => {
      if (!text || text === '{category}') return ''
      return text.includes('/') ? text.split('/').slice(1).join('/') : text
    })

    // Remove Y grid line.
    yAxisModule.get('renderer').grid.template.set('forceHidden', true)
    yAxisAction.get('renderer').grid.template.set('forceHidden', true)
    xAxis.get('renderer').grid.template.set('forceHidden', true)

    dataRange.forEach((item, idx, array) => {
      let range = yAxisModule.makeDataItem({})
      yAxisModule.createAxisRange(range)

      range.set('category', item.startRangeModule.categoryLevel)
      range.set('endCategory', item.endRangeModule.categoryLevel)

      let label = range.get('label')

      label.setAll({
        text: item.endRangeModule.module
      })

      let tick = range.get('tick')
      tick.setAll({
        visible: true,
        strokeOpacity: 0.3,
        length: `-${viewport.width - 40}`,
        location: 0
      })

      let grid = range.get('grid')
      grid.setAll({ strokeOpacity: 1 })

      // add range for the last grid.
      if (idx === array.length - 1) {
        let lastRange = yAxisModule.makeDataItem({})
        yAxisModule.createAxisRange(lastRange)
        lastRange.set('category', item.endRangeModule.categoryLevel)
        let lastTick = lastRange.get('tick')
        lastTick.setAll({
          visible: true,
          strokeOpacity: 0.3,
          length: `-${viewport.width - 40}`,
          location: 1
        })
        let lastGrid = lastRange.get('grid')
        lastGrid.setAll({
          strokeOpacity: 1,
          location: 1
        })
        let lastLabel = lastRange.get('label')
        lastLabel.setAll({
          visible: false
        })
      }
    })

    // Create series.
    const createSerie = (nameLine, valueX, valueY, data) => {
      // Add series
      let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: nameLine,
          alignLabels: true,
          xAxis: xAxis,
          yAxis: yAxisAction,
          baseAxis: yAxisAction,
          valueXField: valueX,
          categoryYField: valueY,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText:
              '[bold]Module:[/] {module}.\n[bold]Action:[/] {categoryY}.\n[bold]Nr Actions:[/] {valueX}.'
          })
        })
      )

      // Set fill color into columns:
      series.columns.template.setAll({
        fillOpacity: 0.8,
        strokeWidth: 0,
        strokeOpacity: 0,
        templateField: 'columnSettings'
      })

      series.data.processor = am5.DataProcessor.new(root, {
        numericFields: [valueX],
        colorFields: ['columnSettings.fill', 'columnSettings.stroke']
      })

      // Set Generals colors of ana-chart.
      series.columns.template.adapters.add('fill', (fill, target) => {
        const dataItem = target.dataItem
        if (dataItem) {
          let getIndex = dataSortIndex.indexOf(dataItem.dataContext.count)
          let opacity = 1
          if (getIndex !== -1) {
            getIndex += 1
            opacity = opacity - getIndex / 10
            if (opacity <= 0) {
              opacity = 0.1
            }
            target.set('fillOpacity', opacity)
          }
        }
        return fill
      })

      series.bullets.push(() => {
        return am5.Bullet.new(root, {
          locationX: 1,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            centerY: am5.p50,
            text: '{valueX}',
            populateText: true,
            fontSize: 10,
            fontWeight: 'bolder'
          })
        })
      })

      series.data.setAll(data)
      yAxisAction.data.setAll(data)
      yAxisModule.data.setAll(data)

      // Make stuff animate on load.
      series.appear(1000)
    }

    // Create serie.
    createSerie(
      'Number of actions count by module',
      'count',
      'categoryLevel',
      dataTotalsActionsByModule,
      'Number of actions'
    )

    chart.appear(1000, 100)

    const ro = new ResizeObserver(() => root.resize())
    if (container) ro.observe(container)

    return () => {
      ro.disconnect()
      root.dispose()
    }
  }, [chartID, dataSet])

  return <div id={chartID} className='logs-actions-module-chart'></div>
}
// Add PropTypes validation.
DashboardLogsActionsByModule.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired
}

export default DashboardLogsActionsByModule
